import { EndUserPages } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import React, { useContext, useEffect } from 'react';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import { MembershipType } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ClosedMembershipNodeNonMembersPageAndParams,
  GroupHubPageAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import BasePage from '../../../components/page/BasePage/BasePage';
import useGroupHubDetails from '../../../components/grouphubs/useGroupHubDetails';
import type { GroupHubDetailsQueryVariables } from '../../../types/graphql-types';
import useNodePageSeoProps from '../../../components/useNodePageSeoProps';

const GroupHubActionMenu = dynamic(
  () => import('../../../components/grouphubs/GroupHubActionMenu/GroupHubActionMenu')
);
const NodeSubscriptionAction = dynamic(
  () => import('../../../components/nodes/NodeSubscriptionAction/NodeSubscriptionAction')
);

/**
 * GroupHub Page
 *
 * @author Amit Agrawal
 */
const GroupHubPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { router } = useEndUserRoutes();
  const { contextNode } = useContext(AppContext);
  const { groupHubId } = router.getPathParams<GroupHubPageAndParams>();

  const variables: GroupHubDetailsQueryVariables = {
    id: contextNode.id,
    useGroupHubPolicies: true,
    useCanAdminNode: true,
    useMembershipInformation: true,
    useManageArticleBlog: true,
    useManageArticleTkb: true
  };

  const {
    queryResult: { data: groupHubDetails },
    grouphubData,
    loading: groupHubInfoLoading
  } = useGroupHubDetails(variables);

  const groupHub = groupHubDetails?.groupHub;
  const nextSeoProps = useNodePageSeoProps(grouphubData);

  const shouldRedirectToNonMemberPage =
    groupHub?.membershipType === MembershipType.Closed &&
    !groupHub?.isMember &&
    !contextNode.userContext.canUpdateNode;

  useEffect(() => {
    if (shouldRedirectToNonMemberPage) {
      router.replaceRoute<ClosedMembershipNodeNonMembersPageAndParams>(
        EndUserPages.ClosedMembershipNodeNonMembersPage,
        {
          groupHubId
        }
      );
    }
  }, [shouldRedirectToNonMemberPage, router, groupHubId]);

  if (groupHubInfoLoading) {
    return null;
  }

  function renderGroupHubActionMenu(): React.ReactElement {
    return <GroupHubActionMenu groupHub={groupHub} />;
  }

  /**
   * Renders the node subscription action.
   */
  function renderNodeSubscriptionAction(): React.ReactElement {
    return <NodeSubscriptionAction node={contextNode} />;
  }

  return (
    !shouldRedirectToNonMemberPage && (
      <BasePage
        pageId={EndUserPages.GroupHubPage}
        breadcrumbActionComponents={[renderNodeSubscriptionAction, renderGroupHubActionMenu]}
        nextSeoProps={nextSeoProps}
      />
    )
  );
};

export default GroupHubPage;
